/**
 * Mobile Helper Utilities for Enhanced Mobile Experience
 */

// Device detection utilities
export const deviceDetection = {
  // Check if device is mobile
  isMobile: () => {
    return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  },

  // Check if device is tablet
  isTablet: () => {
    return window.innerWidth > 768 && window.innerWidth <= 1024;
  },

  // Check if device is desktop
  isDesktop: () => {
    return window.innerWidth > 1024;
  },

  // Check if device supports touch
  isTouchDevice: () => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
  },

  // Check if device is iOS
  isIOS: () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  },

  // Check if device is Android
  isAndroid: () => {
    return /Android/.test(navigator.userAgent);
  },

  // Get device pixel ratio
  getPixelRatio: () => {
    return window.devicePixelRatio || 1;
  },

  // Check if device is in landscape mode
  isLandscape: () => {
    return window.innerWidth > window.innerHeight;
  },

  // Check if device is in portrait mode
  isPortrait: () => {
    return window.innerHeight > window.innerWidth;
  }
};

// Viewport utilities
export const viewport = {
  // Get viewport width
  getWidth: () => {
    return Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
  },

  // Get viewport height
  getHeight: () => {
    return Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
  },

  // Get safe area insets (for devices with notches)
  getSafeAreaInsets: () => {
    const style = getComputedStyle(document.documentElement);
    return {
      top: parseInt(style.getPropertyValue('env(safe-area-inset-top)')) || 0,
      right: parseInt(style.getPropertyValue('env(safe-area-inset-right)')) || 0,
      bottom: parseInt(style.getPropertyValue('env(safe-area-inset-bottom)')) || 0,
      left: parseInt(style.getPropertyValue('env(safe-area-inset-left)')) || 0
    };
  },

  // Check if viewport height is small (landscape mobile)
  isShortViewport: () => {
    return viewport.getHeight() < 500;
  }
};

// Touch and gesture utilities
export const touchUtils = {
  // Add touch-friendly class to body
  enableTouchMode: () => {
    document.body.classList.add('touch-device');
  },

  // Remove touch-friendly class from body
  disableTouchMode: () => {
    document.body.classList.remove('touch-device');
  },

  // Prevent zoom on double tap (iOS)
  preventDoubleTabZoom: (element) => {
    let lastTouchEnd = 0;
    element.addEventListener('touchend', (event) => {
      const now = (new Date()).getTime();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    }, false);
  },

  // Add haptic feedback (if supported)
  hapticFeedback: (type = 'light') => {
    if (navigator.vibrate) {
      const patterns = {
        light: [10],
        medium: [20],
        heavy: [30],
        success: [10, 50, 10],
        error: [50, 50, 50]
      };
      navigator.vibrate(patterns[type] || patterns.light);
    }
  }
};

// Performance utilities for mobile
export const performance = {
  // Debounce function for scroll/resize events
  debounce: (func, wait, immediate) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
  },

  // Throttle function for frequent events
  throttle: (func, limit) => {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  // Lazy load images
  lazyLoadImages: () => {
    const images = document.querySelectorAll('img[data-src]');
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.classList.remove('lazy');
          imageObserver.unobserve(img);
        }
      });
    });

    images.forEach(img => imageObserver.observe(img));
  },

  // Reduce animations for better performance on mobile
  reduceAnimations: () => {
    if (deviceDetection.isMobile()) {
      document.body.classList.add('reduce-motion');
    }
  }
};

// Form utilities for mobile
export const formUtils = {
  // Prevent zoom on input focus (iOS)
  preventInputZoom: () => {
    const inputs = document.querySelectorAll('input, select, textarea');
    inputs.forEach(input => {
      if (input.style.fontSize !== '16px') {
        input.style.fontSize = '16px';
      }
    });
  },

  // Auto-resize textarea
  autoResizeTextarea: (textarea) => {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  },

  // Format phone number input
  formatPhoneNumber: (input) => {
    let value = input.value.replace(/\D/g, '');
    if (value.length >= 6) {
      value = value.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    } else if (value.length >= 3) {
      value = value.replace(/(\d{3})(\d{0,3})/, '($1) $2');
    }
    input.value = value;
  }
};

// Navigation utilities
export const navigationUtils = {
  // Smooth scroll to element
  smoothScrollTo: (element, offset = 0) => {
    const elementPosition = element.offsetTop - offset;
    window.scrollTo({
      top: elementPosition,
      behavior: 'smooth'
    });
  },

  // Check if element is in viewport
  isInViewport: (element) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  },

  // Handle mobile menu toggle
  toggleMobileMenu: (menuElement, toggleButton, overlayElement) => {
    const isOpen = menuElement.classList.contains('open');

    if (isOpen) {
      menuElement.classList.remove('open');
      toggleButton.classList.remove('active');
      if (overlayElement) overlayElement.classList.remove('open');
      document.body.style.overflow = '';
    } else {
      menuElement.classList.add('open');
      toggleButton.classList.add('active');
      if (overlayElement) overlayElement.classList.add('open');
      document.body.style.overflow = 'hidden';
    }

    return !isOpen;
  }
};

// Accessibility utilities
export const a11yUtils = {
  // Add focus visible polyfill
  addFocusVisible: () => {
    document.body.classList.add('js-focus-visible');
  },

  // Announce to screen readers
  announce: (message, priority = 'polite') => {
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    announcer.textContent = message;
    document.body.appendChild(announcer);

    setTimeout(() => {
      document.body.removeChild(announcer);
    }, 1000);
  },

  // Trap focus within element
  trapFocus: (element) => {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    element.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    });
  }
};

// Initialize mobile optimizations
export const initMobileOptimizations = () => {
  // Add device classes to body
  if (deviceDetection.isMobile()) {
    document.body.classList.add('is-mobile');
  }
  if (deviceDetection.isTablet()) {
    document.body.classList.add('is-tablet');
  }
  if (deviceDetection.isDesktop()) {
    document.body.classList.add('is-desktop');
  }
  if (deviceDetection.isTouchDevice()) {
    touchUtils.enableTouchMode();
  }
  if (deviceDetection.isIOS()) {
    document.body.classList.add('is-ios');
  }
  if (deviceDetection.isAndroid()) {
    document.body.classList.add('is-android');
  }

  // Prevent input zoom on iOS
  if (deviceDetection.isIOS()) {
    formUtils.preventInputZoom();
  }

  // Add focus visible support
  a11yUtils.addFocusVisible();

  // Reduce animations on mobile for better performance
  performance.reduceAnimations();

  // Initialize lazy loading
  if ('IntersectionObserver' in window) {
    performance.lazyLoadImages();
  }

  // Handle orientation change
  window.addEventListener('orientationchange', () => {
    setTimeout(() => {
      // Recalculate viewport height after orientation change
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }, 100);
  });

  // Set initial viewport height
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);

  // Handle resize events
  const handleResize = performance.debounce(() => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }, 250);

  window.addEventListener('resize', handleResize);
};

// Export all utilities as default
export default {
  deviceDetection,
  viewport,
  touchUtils,
  performance,
  formUtils,
  navigationUtils,
  a11yUtils,
  initMobileOptimizations
};